import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import PostCard from '../components/post/PostCard';
import { Skeleton } from '../components/ui/skeleton';
import { Bookmark } from 'lucide-react';

export default function BookmarksPage() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBookmarks = async () => {
      try {
        const response = await api.get('/bookmarks');
        setPosts(response.data);
      } catch (error) {
        console.error('Error fetching bookmarks:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBookmarks();
  }, []);

  const handlePostUpdate = (postId, updates) => {
    setPosts(prev => prev.map(post => 
      post.id === postId ? { ...post, ...updates } : post
    ));
  };

  const handleRemoveBookmark = (postId) => {
    setPosts(prev => prev.filter(post => post.id !== postId));
  };

  return (
    <div className="max-w-2xl mx-auto px-4 min-[900px]:ml-60" data-testid="bookmarks-page">
      {/* Header */}
      <div className="py-6">
        <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
          <Bookmark className="w-6 h-6 text-primary fill-primary/20" />
          Bookmarks
        </h1>
        <p className="text-muted-foreground mt-1">Posts you've saved for later</p>
      </div>

      {/* Bookmarked Posts */}
      <div className="space-y-6 pb-8">
        {loading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="glass-card p-4 space-y-4">
              <div className="flex items-center gap-3">
                <Skeleton className="w-10 h-10 rounded-full" />
                <div className="space-y-2">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-3 w-16" />
                </div>
              </div>
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-64 w-full rounded-xl" />
            </div>
          ))
        ) : posts.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
              <Bookmark className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="font-heading text-lg font-bold mb-2">No bookmarks yet</h3>
            <p className="text-muted-foreground">
              Save posts by clicking the bookmark icon to see them here.
            </p>
          </div>
        ) : (
          posts.map(post => (
            <PostCard
              key={post.id}
              post={post}
              onUpdate={handlePostUpdate}
              onBookmarkRemove={handleRemoveBookmark}
            />
          ))
        )}
      </div>
    </div>
  );
}
